import { type FC } from 'react';
import { ChevronRightSpriteIcon } from '@bridebook/web/app-basic/components/header/header-svg-sprite';
import {
  NavigationOptionVariant,
  TNavigationOption,
  TNavigationOptionLink,
  TNavigationOptionNest,
} from './types';
import { isNavigationOptionWithChildren } from './utils';

// State shared by both row variants; rendered as data-* attributes and styled css.
interface IStateProps {
  isShown: boolean;
  isNotFirstVisibleItem: boolean;
}

type INavigationItemNestProps = Pick<
  TNavigationOptionNest,
  'text' | 'icon' | 'disabled' | 'variant'
> &
  IStateProps & {
    onClick?: () => void;
  };

const NavigationItemNest: FC<INavigationItemNestProps> = ({
  onClick,
  text,
  icon,
  disabled,
  variant,
  isShown,
  isNotFirstVisibleItem,
}) => (
  <div
    onClick={disabled ? undefined : onClick}
    className="bcv bci"
    data-shown={isShown}
    data-not-first={isNotFirstVisibleItem}
    data-variant={variant}>
    <div>
      {icon}
      {text}
    </div>
    {!disabled && !!onClick && <ChevronRightSpriteIcon width={8} height={14} />}
  </div>
);

type TNavigationItemLinkProps = Omit<TNavigationOptionLink, 'href'> &
  IStateProps & {
    href?: string;
  };

const NavigationItemLink: FC<TNavigationItemLinkProps> = ({
  text,
  href,
  icon,
  disabled,
  variant,
  isShown,
  isNotFirstVisibleItem,
}) => (
  <a
    className="bci"
    href={href}
    data-shown={isShown}
    data-not-first={isNotFirstVisibleItem}
    data-variant={variant}>
    <div className="bcv">
      <div>
        {icon}
        {text}
      </div>
      {!disabled && variant === NavigationOptionVariant.SingleWithChevron && (
        <ChevronRightSpriteIcon width={8} height={14} />
      )}
    </div>
  </a>
);

interface IProps extends IStateProps {
  option: TNavigationOption;
  onClick?: () => void;
}

export const NavigationOption: FC<IProps> = ({
  option,
  onClick,
  isShown,
  isNotFirstVisibleItem,
}) => {
  if (isNavigationOptionWithChildren(option) && (option.children.length > 0 || !option.href))
    return (
      <NavigationItemNest
        disabled={option.children.length === 0}
        {...option}
        onClick={onClick}
        isShown={isShown}
        isNotFirstVisibleItem={isNotFirstVisibleItem}
      />
    );

  return (
    <NavigationItemLink
      {...option}
      isShown={isShown}
      isNotFirstVisibleItem={isNotFirstVisibleItem}
    />
  );
};
